import { useRef, useState } from "react";
import { css } from "@emotion/react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import type { GenericConfiguration } from "@thalamos/aspire-common";

import { apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { breakpoints } from "../../styles/themeCommon.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  MenuFlyout,
} from "~/components/design-system/index.js";

function GenericInstanceRow({
  genericInstance,
}: {
  genericInstance: GenericConfiguration;
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const options: MenuOptionsType[] = [
    {
      name: "Edit",
      onClick: () => {
        navigate(
          routeFns.genericInstanceCreateEdit(genericInstance.instanceId),
        );
      },
      icon: "edit",
      disabled: false,
    },
  ];

  return (
    <TableRow>
      <TableCell>{genericInstance.displayName}</TableCell>
      <TableCell>{genericInstance.odsCode}</TableCell>
      <TableCell>{genericInstance.instanceId}</TableCell>
      <TableCell>
        <Box
          ref={cardRef}
          onClick={() => {
            setIsMenuOpen(true);
          }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={options}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function GenericInstancesManagementPage() {
  const [genericInstancesFilterQuery, setGenericInstancesFilterQuery] =
    useState("");
  const [page, setPage] = useState(0);
  const genericInstancesPageSize = 10;
  const theme = useTheme();
  const navigate = useNavigate();
  const [{ data: genericInstances, loading: loadingGenericInstances, error }] =
    apiHooks.generic.search(
      genericInstancesFilterQuery,
      genericInstancesPageSize,
      page * genericInstancesPageSize,
    );

  const numPages =
    genericInstances && genericInstances.count
      ? Math.ceil(genericInstances.count / genericInstancesPageSize)
      : 1;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <TextField
          placeholder={"Filter generic instances..."}
          sx={{ width: "100%" }}
          value={genericInstancesFilterQuery}
          onChange={(e) => {
            setGenericInstancesFilterQuery(e.target.value);
            setPage(0);
          }}
        />
        <Box
          css={css`
            @media (max-width: ${breakpoints.values.sm}px) {
              margin-left: ${theme.spacing(2)};
            }

            display: flex;
            margin-bottom: ${theme.spacing(2)};
            margin-left: ${theme.spacing(2)};
          `}
        >
          <Button
            variant={"contained"}
            label={"Create"}
            endIcon={ButtonIcon.add}
            onClick={() => {
              navigate(routeFns.genericInstanceCreateEdit(v4()));
            }}
          />
        </Box>
      </Box>
      {!genericInstances && loadingGenericInstances && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingGenericInstances && error && (
        <Banner bannerType={BannerList.ERROR} title={error.message} />
      )}

      {!loadingGenericInstances &&
        genericInstances &&
        (genericInstances.results.length > 0 ? (
          <Box>
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  if (page > 0) {
                    setPage(page - 1);
                  }
                }}
              >
                &#60;
              </button>
              <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
                Page {page + 1} of {numPages}
              </Box>
              <button
                onClick={() => {
                  if (page < numPages - 1) {
                    setPage(page + 1);
                  }
                }}
              >
                &#62;
              </button>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ODS Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Instance Id</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {genericInstances.results?.map((genericInstance, index) => (
                    <GenericInstanceRow
                      key={index}
                      genericInstance={genericInstance}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <p>no generic instances found</p>
        ))}
    </Box>
  );
}
